import type { AnswerRecord, Course, CourseResult } from '../core/course.js'
import { normalizeCourse } from './normalize.js'
import type { RawCourse, RawLoginResponse } from './types.js'

/**
 * ZAMRZLÝ INTEGRAČNÍ KONTRAKT s placeným předplatným na safetutor.cz.
 *
 * Tohle je jediný soubor v aplikaci, který zná formáty safetutor.cz.
 * Zbytek kódu pracuje s doménovými typy z `core/` a tyhle řetězce nikdy nevidí.
 *
 * Změna čehokoli níže znamená, že zákazník zaplatí školení, které se mu nezapíše.
 * Před úpravou přečíst skill `safetutor-contract`.
 */

/**
 * Adresa API. Výchozí je ostrá `safetutor.cz`; testovací nasazení si ji přepíše
 * přes `VITE_API_BASE` při buildu. Formáty níže se tím nemění — jen host.
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://www.safetutor.cz/api/'

/* ------------------------------------------------------------------ *
 * Přihlášení
 * ------------------------------------------------------------------ */

export function buildLoginUrl(username: string): string {
  return `${API_BASE}?username=${username}`
}

export type LoginResult =
  | { ok: true; userId: string; lecture: string }
  | { ok: false; reason: 'rejected' | 'network' | 'malformed' }

/**
 * Odpověď `{"stav":"OK","id":"30","kurz":"91"}` → lecture "modul91".
 * Cokoli jiného než stav "OK" je odmítnuté přihlášení.
 */
export function parseLoginResponse(raw: unknown): LoginResult {
  const data = raw as RawLoginResponse | null

  if (!data || typeof data !== 'object') return { ok: false, reason: 'malformed' }
  if (data.stav !== 'OK') return { ok: false, reason: 'rejected' }
  if (data.id == null || data.kurz == null) return { ok: false, reason: 'malformed' }

  return { ok: true, userId: String(data.id), lecture: `modul${data.kurz}` }
}

/* ------------------------------------------------------------------ *
 * Načtení kurzu
 * ------------------------------------------------------------------ */

export function buildCourseUrl(lecture: string): string {
  return `${API_BASE}${lecture}.json`
}

export function parseCourse(raw: unknown): Course {
  return normalizeCourse((raw ?? {}) as RawCourse)
}

/* ------------------------------------------------------------------ *
 * Dokončení kurzu — nejcitlivější část kontraktu
 * ------------------------------------------------------------------ */

/**
 * Formát: `{totalMaxPoints}:{totalPoints}:{pointsToPassPercent}:{passed 0|1};`
 *
 * Pozor na dělení nulou: modul bez správně zodpovězené otázky dá
 * `0/0 = NaN`, `NaN >= x` je v JS false → passed "0". Původní aplikace
 * se chovala stejně; replikujeme včetně toho.
 */
export function serializePoints(result: CourseResult): string {
  const passed = result.passed ? '1' : '0'
  return `${result.totalMaxPoints}:${result.totalPoints}:${result.pointsToPassPercent}:${passed};`
}

/**
 * Formát: `Obj:{legacyIndex}:{selectedAnswer}:{wasCorrect 0|1}:{timeSec};`
 * zřetězeno za sebe v pořadí, v jakém uživatel odpovídal.
 */
export function serializeStats(answers: readonly AnswerRecord[]): string {
  let out = ''
  for (const a of answers) {
    out += `Obj:${a.legacyIndex}:${a.selectedAnswer}:${a.wasCorrect ? '1' : '0'}:${a.timeSec};`
  }
  return out
}

export interface FinishParams {
  userId: string
  /** Hodnota `lecture` — např. "modul91" nebo slug demo kurzu. */
  moduleId: string
  result: CourseResult
}

/**
 * ⚠️ Hodnoty se ZÁMĚRNĚ neurl-enkódují — `:` a `;` jdou do query stringu syrové.
 * Server to takhle očekává. Nepřidávat `encodeURIComponent`.
 *
 * Tahle funkce URL pouze sestaví. Navigaci provádí UI vrstva, aby šlo
 * kontrakt testovat bez jakéhokoli síťového volání.
 */
export function buildFinishUrl({ userId, moduleId, result }: FinishParams): string {
  const points = serializePoints(result)
  const stats = serializeStats(result.answers)
  return `${API_BASE}?finish_id=${userId}&mod_id=${moduleId}&points=${points}&stats=${stats}`
}

/* ------------------------------------------------------------------ *
 * Síťová vrstva
 * ------------------------------------------------------------------ */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly kind: 'network' | 'http' | 'parse',
    readonly status?: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Po jaké době vzdát čekání na odpověď serveru.
 *
 * Bez limitu se dá kurz zaseknout natrvalo: firemní proxy nebo přihlašovací
 * portál veřejné Wi-Fi spojení nepřeruší, ale ani neodpoví — `fetch` pak visí
 * bez konce a uživatel kouká na točící se kolečko bez hlášky i bez tlačítka.
 * S limitem z toho vypadne obyčejná chyba sítě, na kterou už UI umí nabídnout
 * „Zkusit znovu".
 */
const REQUEST_TIMEOUT_MS = 15_000

/** Spojí vnější zrušení (odchod z obrazovky) s časovým limitem. */
function withTimeout(signal?: AbortSignal): AbortSignal {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  if (!signal) return timeout
  // `AbortSignal.any` je novější; když chybí, zůstane aspoň samotný limit.
  return typeof AbortSignal.any === 'function' ? AbortSignal.any([signal, timeout]) : timeout
}

async function getJson(url: string, signal?: AbortSignal): Promise<unknown> {
  let response: Response
  try {
    response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      signal: withTimeout(signal),
    })
  } catch {
    throw new ApiError(`Nepodařilo se spojit se serverem: ${url}`, 'network')
  }

  if (!response.ok) {
    throw new ApiError(`Server odpověděl ${response.status}`, 'http', response.status)
  }

  // Server posílá modul4.json s UTF-8 BOM, na kterém by response.json() spadl.
  const text = await response.text()
  try {
    return JSON.parse(text.charCodeAt(0) === 0xfeff ? text.slice(1) : text)
  } catch {
    throw new ApiError('Server vrátil neplatný JSON', 'parse')
  }
}

export async function login(username: string, signal?: AbortSignal): Promise<LoginResult> {
  try {
    return parseLoginResponse(await getJson(buildLoginUrl(username), signal))
  } catch {
    return { ok: false, reason: 'network' }
  }
}

export async function fetchCourse(lecture: string, signal?: AbortSignal): Promise<Course> {
  return parseCourse(await getJson(buildCourseUrl(lecture), signal))
}
